import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AssignRoleRequest,
  CreateRoleRequest,
  EffectivePermissions,
  PagedResult,
  PermissionAuditLog,
  Role,
  SetRolePermissionsRequest,
  UpdateRoleRequest,
  UserRole,
} from './models/role.models';

// Thin wrapper over RolesController in BC_Identity. Authorization is enforced server-side —
// 403s surface as RestException with the right HTTP status, so callers don't need to gate
// these calls themselves. UI gating (hiding the admin nav for non-admins) goes through
// PermissionsService.isPermissionsAdmin() instead.
@Injectable({ providedIn: 'root' })
export class RolesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiConfig.IdentityAPI.endpoint;

  // ─── Templates ───────────────────────────────────────────────────────────────────────
  listTemplates(): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.baseUrl}/role-templates`);
  }
  cloneTemplate(templateId: number, orgId: number): Observable<Role> {
    return this.http.post<Role>(
      `${this.baseUrl}/organizations/${orgId}/roles/from-template/${templateId}`,
      {}
    );
  }

  // ─── Roles ───────────────────────────────────────────────────────────────────────────
  listRoles(orgId: number): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.baseUrl}/organizations/${orgId}/roles`);
  }
  getRole(roleId: number): Observable<Role> {
    return this.http.get<Role>(`${this.baseUrl}/roles/${roleId}`);
  }
  createRole(orgId: number, body: CreateRoleRequest): Observable<Role> {
    return this.http.post<Role>(`${this.baseUrl}/organizations/${orgId}/roles`, body);
  }
  updateRole(roleId: number, body: UpdateRoleRequest): Observable<Role> {
    return this.http.put<Role>(`${this.baseUrl}/roles/${roleId}`, body);
  }
  deleteRole(roleId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/roles/${roleId}`);
  }
  setRolePermissions(roleId: number, body: SetRolePermissionsRequest): Observable<Role> {
    return this.http.put<Role>(`${this.baseUrl}/roles/${roleId}/permissions`, body);
  }

  // ─── Assignments ─────────────────────────────────────────────────────────────────────
  listUserRoles(userId: number): Observable<UserRole[]> {
    return this.http.get<UserRole[]>(`${this.baseUrl}/users/${userId}/roles`);
  }
  assignRole(userId: number, body: AssignRoleRequest): Observable<UserRole> {
    return this.http.post<UserRole>(`${this.baseUrl}/users/${userId}/roles`, body);
  }
  removeUserRole(userId: number, userRoleId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/users/${userId}/roles/${userRoleId}`);
  }

  // ─── Reads ───────────────────────────────────────────────────────────────────────────
  getEffectivePermissions(userId: number, companyId: number): Observable<EffectivePermissions> {
    return this.http.get<EffectivePermissions>(
      `${this.baseUrl}/users/${userId}/companies/${companyId}/effective-permissions`
    );
  }

  getAuditLog(
    orgId: number,
    opts: {
      pageNumber?: number;
      pageSize?: number;
      from?: string;
      to?: string;
      actorUserId?: number;
      targetUserId?: number;
    } = {}
  ): Observable<PagedResult<PermissionAuditLog>> {
    let params = new HttpParams()
      .set('pageNumber', String(opts.pageNumber ?? 1))
      .set('pageSize', String(opts.pageSize ?? 50));
    if (opts.from) params = params.set('from', opts.from);
    if (opts.to) params = params.set('to', opts.to);
    if (opts.actorUserId != null) params = params.set('actorUserId', String(opts.actorUserId));
    if (opts.targetUserId != null) params = params.set('targetUserId', String(opts.targetUserId));

    return this.http.get<PagedResult<PermissionAuditLog>>(
      `${this.baseUrl}/organizations/${orgId}/permission-audit-log`,
      { params }
    );
  }
}
